import type { ShipTier } from "@voidex/contracts-ts";
import { useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { contractConfigs } from "../web3/contracts";

export function useShipBalance(address?: `0x${string}`) {
	const { data, isLoading, error, refetch } = useReadContract({
		...contractConfigs.shipNFT,
		functionName: "balanceOf",
		args: address ? [address] : undefined,
		query: {
			enabled: !!address,
			refetchInterval: 10_000,
		},
	});

	return {
		balance: Number(data ?? 0),
		isLoading,
		error,
		refetch,
	};
}

export function useShipOwner(tokenId: bigint) {
	const { data, isLoading, error } = useReadContract({
		...contractConfigs.shipNFT,
		functionName: "ownerOf",
		args: [tokenId],
		query: {
			enabled: tokenId > 0n,
		},
	});

	return {
		owner: data,
		isLoading,
		error,
	};
}

export function useShipTier(tokenId: bigint) {
	const { data, isLoading, error } = useReadContract({
		...contractConfigs.shipNFT,
		functionName: "shipTier",
		args: [tokenId],
		query: {
			enabled: tokenId > 0n,
		},
	});

	return {
		tier: data !== undefined ? (Number(data) as ShipTier) : undefined,
		isLoading,
		error,
	};
}

export function useIsApprovedForAll(owner?: `0x${string}`, operator?: `0x${string}`) {
	const { data, isLoading, refetch } = useReadContract({
		...contractConfigs.shipNFT,
		functionName: "isApprovedForAll",
		args: owner && operator ? [owner, operator] : undefined,
		query: {
			enabled: !!owner && !!operator,
		},
	});

	return {
		isApproved: data ?? false,
		isLoading,
		refetch,
	};
}

export function useApproveShip() {
	const { writeContractAsync, data: hash, isPending, error, reset } = useWriteContract();
	const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
		hash,
	});

	const approve = async (to: `0x${string}`, tokenId: bigint) => {
		return writeContractAsync({
			...contractConfigs.shipNFT,
			functionName: "approve",
			args: [to, tokenId],
		});
	};

	return {
		approve,
		hash,
		isPending: isPending || isConfirming,
		isSuccess,
		error,
		reset,
	};
}

export function useApproveShipForFarming() {
	const { approve, isPending, isSuccess, error, reset } = useApproveShip();
	const { preTgeFarming } = contractConfigs;

	const approveForFarming = async (tokenId: bigint) => {
		return approve(preTgeFarming.address, tokenId);
	};

	return {
		approveForFarming,
		isPending,
		isSuccess,
		error,
		reset,
	};
}
